import { useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { FullPage, TextHeader, LoadingPage, ErrorPage, useNotifications } from '@wallyhealth/web-core'
import { useStandbyScheduleSlots, useFetchFulfilledSlots, useScheduleStandbySlot } from '../hooks'
import styles from './index.module.css'
import intelService from 'services/intelService'
import BookingAction from 'components/schedule/BookingAction'
import ScheduleTime from 'components/schedule/ScheduleTime'

const BOOKING_ERROR_MAP = {
  standby_hold_taken: 'The time you attempted to book was taken by another patient, please pick another time',
  standby_outreach_expired: 'Sorry, all spots have been snagged'
}

const generateGoogleMapsLink = (address) => `https://maps.google.com?q=${address.replace(' ', '+')}+New+York`

const StandbySlotLocation = ({ locationName, slots, unavailableSlotIds = [], addressMap, slotValue, setSlotValue, setIsBooking }) => {
  const trackMapLinkClicked = (locationName) => {
    intelService.recordClick({
      clickName: 'standby-location-map-link',
      viewName: window.location.href,
      properties: {
        locationName
      }
    })
  }

  const locationAddress = addressMap[locationName]

  return (
    <div className={styles.location}>
      <div className={styles.info}>
        <h3 className={styles.name}>
          {locationName}
        </h3>
        <a className={styles.link}
          target='_blank'
          rel='noopener noreferrer'
          onClick={() => trackMapLinkClicked(locationName)}
          href={generateGoogleMapsLink(locationAddress)}>{locationAddress}
        </a>
      </div>
      <div className={styles.slots}>
        {slots
          .filter(slot => !unavailableSlotIds.includes(slot.id))
          .map(slot => {
            const isSelected = slotValue === slot.id

            return (
              <ScheduleTime
                key={slot.id}
                time={slot.startTimeLocal}
                isSelected={isSelected}
                onSelected={() => {
                  setSlotValue(slot.id)
                  setIsBooking(true)
                }}
              />
            )
          })}
      </div>
    </div>
  )
}

const StandbyPage = ({ linkId }) => {
  const navigate = useNavigate()
  const { fulfilledSlotIds } = useFetchFulfilledSlots({ linkId })

  const [slotValue, setSlotValue] = useState(null)
  const [isBooking, setIsBooking] = useState(false)
  const [isOutreachExpired, setIsOutreachExpired] = useState(false)

  const { schedulingData, isLoadingSlots } = useStandbyScheduleSlots({ linkId })
  const { slots: locationSlotMap, cohortId, addressMap } = schedulingData ?? {}

  const { scheduleStandbySlot } = useScheduleStandbySlot({ linkId })

  const notifications = useNotifications()

  useEffect(() => {
    intelService.recordView({
      viewName: 'StandbyPage',
      properties: {
        linkId,
        cohortId
      }
    })
  }, [])

  const onBookingConfirmed = () => {
    scheduleStandbySlot.mutate({ slotId: slotValue, cohortId }, {
      onSuccess: () => {
        intelService.recordEvent({
          eventName: 'booked-standby-appointment',
          properties: {
            linkId,
            cohortId,
            slotValue
          }
        })
        navigate('/standby-schedule/confirmed')
      },
      onError: (error) => {
        intelService.recordEvent({
          eventName: 'failed-booking-standby-appointment',
          properties: {
            linkId,
            cohortId,
            slotValue,
            errorMessage: error.message
          }
        })

        if (error.message === 'standby_outreach_expired') {
          setIsOutreachExpired(true)
        }

        notifications.error(BOOKING_ERROR_MAP[error.message] ?? 'We encountered a problem. Please try again.')
        setIsBooking(false)
      }
    })
  }

  const onBookingCancelled = () => {
    setIsBooking(false)
    setSlotValue(null)
  }

  if (isLoadingSlots) {
    return (
      <LoadingPage />
    )
  }

  const hasAvailableSlots = locationSlotMap && Object.keys(locationSlotMap).some(location => {
    const availableSlots = locationSlotMap[location].filter(slot =>
      !fulfilledSlotIds.includes(slot.id)
    )
    return availableSlots.length > 0
  })

  if (isOutreachExpired || (locationSlotMap && !hasAvailableSlots)) {
    return (
      <ErrorPage
        title="All booked up!"
        description="All spots for tomorrow have been taken, but don't worry, we'll notify you as soon as new appointments become available."
      />
    )
  }

  return (
    <FullPage bodyClassName={styles.container}>
      <div className={styles.body}>
        <TextHeader
          title="Snag a time that works for you tomorrow."
          subtitle="If your preferred location isn't shown, it's because there are no available times."
          className={styles.header}
        />

        <div className={styles.locations}>
          {Object.keys(locationSlotMap)
            .filter(location => {
              const availableSlots = locationSlotMap[location].filter(slot =>
                !fulfilledSlotIds.includes(slot.id)
              )
              return availableSlots.length > 0
            })
            .map(location => (
              <StandbySlotLocation
                key={location}
                addressMap={addressMap}
                locationName={location}
                slots={locationSlotMap[location]}
                unavailableSlotIds={fulfilledSlotIds}
                slotValue={slotValue}
                setSlotValue={setSlotValue}
                setIsBooking={setIsBooking}
              />
            ))
          }
        </div>

        {isBooking &&
          <BookingAction
            onCanceled={onBookingCancelled}
            onTriggered={() => onBookingConfirmed()}
          />
        }
      </div>
    </FullPage>
  )
}

export default StandbyPage
